<script setup>
import { ref, onMounted } from 'vue'
import { formattedDate, swalError, formatCurrency, getInvStatusColor } from '@/helper'
import { getInvoiceSummary } from '@/services/payments';

const props = defineProps({
  monthlyTotal: {
    type: Number,
    default: 0
  },
  latestInvoices: {
    type: Array,
    default: () => []
  }
})

const isLoading = ref(true)
const monthlyTotal = ref(0)
const latestInvoices = ref([])

onMounted(async () => {
  await fetchInvoiceData()
  isLoading.value = false
})

async function fetchInvoiceData() {
  try {
    const response = await getInvoiceSummary()
    if (!response.error) {
      monthlyTotal.value = response.data.monthly_total || 0
      latestInvoices.value = response.data.latest_invoices || []
    } else {
      swalError(response.message)
    }
  } catch (error) {
    swalError('Failed to load invoice data', true)
  }
}

function getStatusIcon(status) {
  const icons = {
    paid: 'check-circle',
    pending: 'clock',
    overdue: 'alert-circle',
    draft: 'file-text'
  }
  return icons[status] || 'file-text'
}
</script>

<template>
  <loadingCard v-if="isLoading"/>
  <div v-else class="card invoice-summary-card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5>Invoice Summary</h5>
      <router-link :to="{name: 'payments-history'}" class="text-secondary">
        <vue-feather type="external-link" class="fs-6"></vue-feather>
      </router-link>
    </div>
    
    <div class="card-body">
      <!-- Monthly Total Section -->
      <div class="monthly-total-section mb-4">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <p class="text-muted mb-1">Total This Month</p>
            <h3 class="text-primary mb-0">{{ formatCurrency(monthlyTotal) }}</h3>
          </div>
          <div class="icon-circle bg-light-primary">
            <vue-feather type="dollar-sign" class="text-primary"></vue-feather>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <hr class="my-3">

      <!-- Latest Invoices Section -->
      <div class="latest-invoices-section">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h6 class="mb-0">Latest Invoices</h6>
          <router-link :to="{name: 'payments-history'}" class="text-primary small">
            View All
          </router-link>
        </div>

        <div class="invoice-list">
          <div 
            v-for="invoice in latestInvoices" 
            :key="invoice.id"
            class="invoice-item d-flex align-items-center justify-content-between py-2"
          >
            <div class="invoice-info">
              <h6 class="mb-1">{{ invoice.invoice_number }}</h6>
              <p class="text-muted mb-0 small">{{ invoice.client_name }}</p>
              <small class="text-muted">{{ formattedDate(invoice.created_at) }}</small>
            </div>
            
            <div class="invoice-amount-status text-end">
              <h6 class="mb-1">{{ formatCurrency(invoice.amount) }}</h6>
              <span 
                :class="`badge bg-light-${getInvStatusColor(invoice.status)} text-${getInvStatusColor(invoice.status)}`"
              >
                <vue-feather 
                  :type="getStatusIcon(invoice.status)" 
                  size="12" 
                  class="me-1"
                ></vue-feather>
                {{ invoice.status }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="latestInvoices.length === 0" class="text-center py-4">
          <vue-feather type="inbox" class="text-muted mb-2" size="24"></vue-feather>
          <p class="text-muted">No invoices found</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.monthly-total-section h3 {
  font-size: 2rem;
  font-weight: 700;
}

.monthly-total-section p {
  color: rgba(255, 255, 255, 0.8);
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.invoice-item {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.invoice-item:hover {
  background-color: #f8f9fa;
}

.invoice-item:last-child {
  border-bottom: none;
}

.invoice-info h6 {
  font-size: 0.9rem;
  font-weight: 600;
}

.invoice-amount-status h6 {
  font-size: 0.9rem;
  font-weight: 700;
  color: #2c323f;
}

.badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 20px;
}

.bg-light-success {
  background-color: rgba(81, 187, 37, 0.1);
}

.bg-light-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-light-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-light-secondary {
  background-color: rgba(108, 117, 125, 0.1);
}
</style>